/* eslint-disable max-depth */
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LightningCurrentLocation } from "@/resource/IconComponents";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Select, Spin } from "antd";
import type { SelectProps } from "antd";
import { AggregationCalcWithHTemp, getLocations, getCountriesData, getLocationsFromServices } from "@/util/http/ClimateShift/index";
import { ICountriesResModel, IGetLocationsResModel } from "@/util/http/ClimateShift/responseType";
import {
    updateCommonWarnMessage,
    updateCommonErrorMessage,
    updateFetchedChartData,
    updateSearchLoading,
    updateSelectedLocationData,
    updateSelectedFavouriteItem,
    updateCountry,
    updateSelectedLocationDataForAverage,
    updateSelectedFavouriteId
} from "@/redux/actions/climateShiftAction";
import { IAggregationCalcWithHTempReqModel } from "@/util/http/ClimateShift/requestType";
import { RootState } from "@/redux/reducer/store";
import "./index.less";

interface IClimateShiftLocationProps {
    childChangeCurrentSelectedCountryCode?: (code: string) => void;
}

const ClimateShiftLocation = (porp: IClimateShiftLocationProps) => {

    const { childChangeCurrentSelectedCountryCode } = porp;

    const dispatch = useDispatch();

    // Redux state
    const selectedLocationData = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationData);
    const selectedFavouriteItem = useSelector((state: RootState) => state.climateShiftReducer.selectedFavouriteItem);

    // Local state
    const [selectedCountry, setSelectedCountry] = useState(undefined);
    const [selectKey, setSelectKey] = useState(0);
    const [countrylist, setCountrylist] = useState<ICountriesResModel[]>([]);
    const initialCountryList = useRef<ICountriesResModel[]>([]);
    const [address, setAddress] = useState(undefined);// Input address value
    const [showResults, setShowResults] = useState(false);// Whether to show the location results list
    const [locationDatas, setLocationDatas] = useState<IGetLocationsResModel[]>([]); // Location search results
    const endYear: number = new Date().getFullYear() - 1;// Default to last year
    const [searchLocationLoading, setSearchLocationLoading] = useState(false);
    const locationSelectRef = useRef<any>(null);
    const skipBlurClearRef = useRef(false);

    // 核心：标记最新请求的唯一标识（用递增数字最简单）
    const latestRequestIdRef = useRef(0);

    // Request options state
    const [requestOptions, setRequestOptions] = useState<IAggregationCalcWithHTempReqModel>({
        startYear: 1995,
        endYear: endYear,
        longitude: null,
        latitude: null,
        postalCode: null,
    });

    // 简单防抖函数
    function debounce(fn, delay) {
        let timer = null;
        return function (...args) {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                fn.apply(this, args);
            }, delay);
        };
    }

    const getCountries = async (value) => {
        try {
            const res = await getCountriesData(value);
            if (res.data && res.data.length > 0) {
                // 过滤掉 areaISO2Code 为空字符串的数据
                const filteredData = res.data.filter(item => item.areaISO2Code && item.areaISO2Code !== "");
                // 存储全部国家数据到 localStorage
                localStorage.setItem("allCountriesData", JSON.stringify(filteredData));
                // Sort by areaName alphabetically for显示
                const sortedData = filteredData.slice().sort((a, b) => {
                    if (!a.areaName) return 1;
                    if (!b.areaName) return -1;
                    return a.areaName.localeCompare(b.areaName);
                });
                setCountrylist(sortedData);
                initialCountryList.current = sortedData;
            }
        }
        catch (error) {
            console.error("Error fetching countries data:", error);
        } finally {
            console.log("Finished fetching countries data.");
        }
    };

    const initFunc = async () => {
        // 优先从 localStorage 读取
        const cached = localStorage.getItem("allCountriesData");
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                // 过滤掉 areaISO2Code 为空字符串的数据
                const filteredData = parsed.filter(item => item.areaISO2Code && item.areaISO2Code !== "");
                // 排序
                const sortedData = filteredData.slice().sort((a, b) => {
                    if (!a.areaName) return 1;
                    if (!b.areaName) return -1;
                    return a.areaName.localeCompare(b.areaName);
                });
                setCountrylist(sortedData);
                initialCountryList.current = sortedData;
            } catch (e) {
                // 解析失败则重新请求
                await getCountries("");
            }
        } else {
            await getCountries("");
        }
    };

    useEffect(() => {
        initFunc();
    }, []);

    useEffect(() => {
        if (typeof childChangeCurrentSelectedCountryCode === "function") {
            childChangeCurrentSelectedCountryCode(selectedCountry);
        }
    }, [selectedCountry]);

    // 自定义过滤逻辑：仅根据 areaName 模糊匹配（不区分大小写）
    const filterOption: SelectProps["filterOption"] = (input, option) => {
        if (!option || !option.children || !input) return true;
        // 获取选项的显示文本（areaName）和输入内容，都转小写进行匹配
        const optionText = option.children.toString().toLowerCase();
        const inputText = input.toLowerCase();
        return optionText.startsWith(inputText);
    };

    const handleCountryChange = (value) => {
        //When making changes, the location related data should be cleared
        setAddress(undefined);// Clear input field
        setLocationDatas([]);// Clear location search results
        setShowResults(false);// Hide results list
        dispatch(updateFetchedChartData([])); // Clear chart data in Redux
        dispatch(updateSelectedLocationData(null)); //Important, Clear selected location data in Redux
        dispatch(updateSelectedLocationDataForAverage(null));
        setSelectedCountry(value);
        dispatch(updateCountry(value)); // Update country in Redux
    };

    // Validate if input is a valid "lng，lat" format
    const isValidLngLat = (input: string) => {
        const parts = input.split(",").map(s => s?.trim());
        if (parts.length !== 2) return false;
        const lng = Number(parts[0]);
        const lat = Number(parts[1]);
        if (isNaN(lat) || isNaN(lng)) return false;
        if (lng < -180 || lng > 180) return false;
        if (lat < -90 || lat > 90) return false;
        return true;
    };

    // 通用搜索函数，供防抖和定位按钮复用
    const searchLocations = async ({ searchText, countryCode }) => {
        // 3. 生成本次请求的唯一标识，并更新"最新标识"
        const currentRequestId = ++latestRequestIdRef.current;
        setSearchLocationLoading(true);
        try {
            const res = await getLocationsFromServices({ searchText, countryCode });

            if (currentRequestId === latestRequestIdRef.current) {
                if (searchText && isValidLngLat(searchText)) {
                    handleSelectLocationData(res.data[0]);
                } else {
                    const filtered = Array.isArray(res.data)
                        ? res.data.filter(item => item.countryCode === countryCode)
                        : [];
                    if (!filtered || filtered.length === 0) {
                        setLocationDatas([]);
                        setShowResults(false);
                        dispatch(updateCommonWarnMessage("No results found for the entered Location / Postal Code."));
                        setAddress(undefined);
                    } else if (filtered.length === 1) {
                        setLocationDatas([filtered[0]]);
                        handleSelectLocationData(filtered[0]);
                    } else {
                        setLocationDatas(filtered);
                        setShowResults(true);
                    }
                }
            } else {
                console.log(`request ${currentRequestId} Expired, ignore results`);
            }

        } catch (error) {
            dispatch(updateCommonErrorMessage("An error occurred. Please try again later."));
            console.error("Error fetching locations:", error);
        } finally {
            if (currentRequestId === latestRequestIdRef.current) {
                setSearchLocationLoading(false);
            }
        }
    };

    // Handle selecting a location from the results list
    const handleSelectLocationData = (item) => {
        dispatch(updateSelectedLocationData(item));
        dispatch(updateSelectedLocationDataForAverage(null));
        dispatch(updateFetchedChartData([])); // Clear chart data in Redux

        setSelectedCountry(item?.countryCode);
        dispatch(updateCountry(item?.countryCode));//Update country in Redux
        if (item?.source?.toLowerCase() === "placename") {
            setAddress(`${item?.displayName} - ${item?.postalCode || ""}`);
        }
        else if (item?.source?.toLowerCase() === "postalcode") {
            setAddress(`${item?.postalCode} - ${item?.placeName || ""}`);
        }
        else {
            setAddress(item?.displayName && item?.displayName?.trim() !== "" ? item?.displayName : item?.placeName || "");
        }
        setShowResults(false);
        skipBlurClearRef.current = true;
        locationSelectRef.current?.blur?.();
    };

    const debouncedFetchLocations = React.useCallback(
        debounce(async (val) => {
            await searchLocations({ searchText: val, countryCode: selectedCountry });
        }, 1000),
        [selectedCountry]
    );

    // Fetch search results based on selected location data
    const getSearchResults = async () => {
        dispatch(updateSelectedLocationDataForAverage(null)); // Clear previous chart data in Redux
        if (!selectedCountry?.trim()) {
            dispatch(updateCommonWarnMessage("Please select a country."));
            return;
        }

        if (!address || address?.trim() === "") {
            dispatch(updateCommonWarnMessage("Please enter a location or postal code."));
            return;
        }

        if (!selectedLocationData) {
            dispatch(updateCommonWarnMessage("Please select a location from the results list."));
            return;
        }


        // Prepare latest options
        let tempRequestOptions;
        if (selectedLocationData) {
            tempRequestOptions = {
                startYear: 1995,
                endYear: endYear,
                longitude: selectedLocationData.lng ?? null,
                latitude: selectedLocationData.lat ?? null,
                postalCode: selectedLocationData.postalCode ?? null
            };
            setRequestOptions(tempRequestOptions); // Optional: keep state in sync
        }
        dispatch(updateSearchLoading(true));

        try {
            const res = await AggregationCalcWithHTemp(tempRequestOptions || requestOptions);
            dispatch(updateFetchedChartData(res?.data ?? []));
        }
        catch (error) {
            dispatch(updateCommonErrorMessage("An error occurred. Please try again later."));
            console.error("Error fetching ClimateShift type contours:", error);
        }
        finally {
            dispatch(updateSearchLoading(false));
            console.log("Finished fetching search results.");
        }
    };

    // Get user's current location using Geolocation API
    const getCurrentLocation = () => {
        dispatch(updateFetchedChartData([])); // Clear chart data in Redux
        dispatch(updateCountry(undefined)); // Clear country in Redux
        setSelectedCountry(undefined);
        setLocationDatas([]);
        setShowResults(false);
        try {
            navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                    const pos = {
                        lng: position.coords.longitude,
                        lat: position.coords.latitude,
                    };
                    if (pos?.lng && pos.lat) {
                        const tmpInputValue = `${pos.lng},${pos.lat}`;
                        setShowResults(false);
                        searchLocations({ searchText: tmpInputValue, countryCode: undefined });
                    }
                },
                (error) => {
                    dispatch(updateCommonWarnMessage("Location not found. Please ensure location services are enabled."));
                }
            );
        } catch (error) {
            dispatch(updateCommonErrorMessage("An unexpected error occurred while fetching your location."));
        }
    };

    // Clear all search-related state and results
    const scrollCountryDropdownTop = () => {
        setTimeout(() => {
            const dropdowns = document.querySelectorAll(".ant-select-dropdown");
            if (dropdowns && dropdowns.length > 0) {
                dropdowns.forEach(dropdown => {
                    dropdown.scrollTop = 0;
                });
            }
        }, 100);
    };

    const handleClearAll = () => {
        setAddress(undefined);// Clear input field
        setLocationDatas([]);// Clear location search results
        setShowResults(false);// Hide results list
        dispatch(updateFetchedChartData([])); // Clear chart data in Redux
        dispatch(updateSelectedLocationData(null)); //Important, Clear selected location data in Redux
        dispatch(updateSelectedLocationDataForAverage(null)); // Clear selected location data for average in Redux
        dispatch(updateSelectedFavouriteItem(null)); // Clear selected favourite item in Redux
        dispatch(updateSelectedFavouriteId(null)); // Clear selected favourite ID in Redux
        setSelectedCountry(undefined);// Clear selected country input field
        dispatch(updateCountry(undefined)); // Clear country in Redux

        setRequestOptions({
            startYear: 1995,
            endYear: endYear,
            longitude: null,
            latitude: null,
            postalCode: null,
        });// Reset request options
        // 重置countrylist为初始顺序
        if (initialCountryList.current.length > 0) {
            setCountrylist(initialCountryList.current);
        }
        scrollCountryDropdownTop();
        setSelectKey(k => k + 1); // 强制Select刷新
    };

    useEffect(() => {
        if (selectedFavouriteItem) {
            setSelectedCountry(selectedFavouriteItem?.selectedLocationItem?.countryCode);
            dispatch(updateCountry(selectedFavouriteItem?.selectedLocationItem?.countryCode)); // Update country in Redux
            if (selectedFavouriteItem?.selectedLocationItem?.source?.toLowerCase() === "postalcode") {
                setAddress(`${selectedFavouriteItem?.selectedLocationItem?.postalCode} - ${selectedFavouriteItem?.selectedLocationItem?.placeName}`);
            } else if (selectedFavouriteItem?.selectedLocationItem?.source?.toLowerCase() === "placename") {
                setAddress(`${selectedFavouriteItem?.selectedLocationItem?.displayName} - ${selectedFavouriteItem?.selectedLocationItem?.postalCode}`);
            } else {
                setAddress(selectedFavouriteItem?.selectedLocationItem?.displayName && selectedFavouriteItem?.selectedLocationItem?.displayName?.trim() !== "" ?
                    selectedFavouriteItem?.selectedLocationItem?.displayName : selectedFavouriteItem?.selectedLocationItem?.placeName || "");
            }
        }
    }, [selectedFavouriteItem]);

    return (
        <div className="climate-shift-location-container">
            <div className="search-area-row">
                <div className="search-area-row-left">
                    <div className="search-label">Country</div>
                    <Select
                        key={selectKey}
                        style={{ width: "100%" }}
                        size="large"
                        value={selectedCountry}
                        onChange={(value) => handleCountryChange(value)}
                        showSearch
                        filterOption={filterOption}
                        optionFilterProp="children"
                        allowClear
                        placeholder="Please select a country"
                    >
                        {countrylist.map((country) => (
                            <Select.Option
                                key={country.areaId}
                                value={country.areaISO2Code}
                            >
                                {country.areaName}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className="search-area-row-right">
                    <div className="search-label">Location / Postal Code</div>
                    <div className="search-input-row">
                        <Spin spinning={searchLocationLoading} style={{ width: "100%" }}>
                            <Select
                                ref={locationSelectRef}
                                size="large"
                                showSearch
                                value={address}
                                placeholder="Enter Location or Postal Code"
                                style={{ width: "100%" }}
                                filterOption={false}
                                notFoundContent={searchLocationLoading ? <Spin size="small" /> : null}
                                onSearch={val => {
                                    setShowResults(false);
                                    const minLen = selectedCountry === "GB" ? 2 : 3;
                                    if (val && val.length >= minLen) {
                                        debouncedFetchLocations(val);
                                    }
                                }}
                                onFocus={() => {
                                    setAddress(undefined);
                                    setShowResults(false);
                                    setLocationDatas([]);
                                    dispatch(updateSelectedLocationDataForAverage(null));
                                    dispatch(updateFetchedChartData([])); // Clear chart data in Redux
                                }}
                                onChange={val => {
                                    setAddress(val);
                                    setShowResults(false);
                                    setLocationDatas([]);
                                    dispatch(updateSelectedLocationData(null)); // Clear selected location in Redux
                                    dispatch(updateSelectedLocationDataForAverage(null)); // Clear selected location for average in Redux
                                    dispatch(updateFetchedChartData([])); // Clear chart data in Redux

                                    const selected = locationDatas.find(item => {
                                        let value = "";
                                        if (item?.source?.toLowerCase() === "placename") {
                                            value = `${item?.displayName} - ${item?.postalCode || ""}`;
                                        } else if (item?.source?.toLowerCase() === "postalcode") {
                                            value = `${item?.postalCode} - ${item?.placeName || ""}`;
                                        } else {
                                            value = item?.displayName || item?.placeName || item?.postalCode;
                                        }
                                        return value === val;
                                    });
                                    if (selected) {
                                        handleSelectLocationData(selected);
                                    }
                                }}
                                allowClear
                            >
                                {showResults && locationDatas && locationDatas.length > 0 && locationDatas.map((item, idx) => {
                                    let value = "";
                                    if (item?.source?.toLowerCase() === "placename") {
                                        value = `${item?.displayName} - ${item?.postalCode || ""}`;
                                    } else if (item?.source?.toLowerCase() === "postalcode") {
                                        value = `${item?.postalCode} - ${item?.placeName || ""}`;
                                    } else {
                                        value = item?.displayName || item?.placeName || item?.postalCode;
                                    }
                                    return (
                                        <Select.Option key={value + idx} value={value}>
                                            {item?.source?.toLowerCase() === "placename"
                                                ? `${item?.displayName} - ${item?.postalCode}`
                                                : item?.source?.toLowerCase() === "postalcode"
                                                    ? `${item?.postalCode} - ${item?.placeName}`
                                                    : `${item?.displayName}`}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </Spin>
                        <Button className="bg-button location-btn"
                            style={{ marginLeft: 8 }}
                            onClick={() => {
                                getCurrentLocation();
                            }}>
                            <LightningCurrentLocation color={"#FFFFFF"} />
                        </Button>
                    </div>
                    {/* {showResults && locationDatas && locationDatas.length > 0 && (
                        <ul className="location-results-list">
                            {locationDatas.map((item, idx) => (
                                <li
                                    key={(item?.placeName || "") + idx}
                                    className="location-result-item"
                                    onClick={() => handleSelectLocationData(item)}
                                >
                                    {item?.source?.toLowerCase() === "placename"
                                        ? `${item?.displayName} - ${item?.postalCode}`
                                        : item?.source?.toLowerCase() === "postalcode"
                                            ? `${item?.postalCode} - ${item?.placeName}`
                                            : `${item?.displayName}`}
                                </li>
                            ))}
                        </ul>
                    )} */}
                </div>
            </div>
            <div className="search-btn-group-left">
                <Button
                    className="border-button clear-btn"
                    onClick={handleClearAll}
                >
                    Clear
                </Button>
                <Button
                    type="primary"
                    className="bg-button search-btn"
                    onClick={getSearchResults}
                >
                    Search
                </Button>
            </div>
        </div>
    );
};

export default ClimateShiftLocation;